// src/utils/studentPreviewCandidates.js
//
// Pass 82D §26/§38 — the Student Preview selector must populate ONLY identities the canonical model can PROVE, and must
// NOT invent a student. A "previewable student" is an Auth participant (a Firebase uid) that OWNS canonical, org-scoped
// student data (a video they uploaded, or a lessonInstance they are the explicit `studentId` of). We NEVER derive a
// student from a commercial `clientId`, from `requesterUserId`, or by name/email matching — those stay distinct (§18).
// If a commercial client has NO linked student Auth account, they are NOT a previewable student (surfaced separately as
// "No student login linked", handled by the caller). READ-ONLY (zero writes).

import { collection, query, where, getDocs } from 'firebase/firestore';
import { PLATFORM_COLLECTIONS } from './platformSchema';
import { toStudentParticipant } from './participantSelfAccess';

// ── Pass 85.1A — CANONICAL preview candidates come from `participantProfiles` (the coached human), NOT from Auth-uid
// ownership of videos/lessons. An operator previewing chooses an org + a PARTICIPANT; the preview then renders the SAME
// safe student data layer (modern org+participant reads). A participant with NO videos/lessons is still previewable
// (identity is the profile, not owned data). Read-only; never fabricates identity; no name/email inference.
export async function loadParticipantPreviewCandidates(db, organizationId = '') {
  const org = String(organizationId || '').trim();
  if (!org) return []; // fail closed — never assume an org
  const snap = await getDocs(query(
    collection(db, PLATFORM_COLLECTIONS.PARTICIPANT_PROFILES),
    where('organizationId', '==', org),
  ));
  return snap.docs
    .map((d) => toStudentParticipant(d.data(), d.id))
    .map((p) => ({
      participantId: p.participantId,
      organizationId: p.organizationId,
      name: p.displayName || p.preferredName || `Participant ${String(p.participantId).slice(-6)}`,
      primaryDiscipline: p.primaryDiscipline,
      accountLinked: !!p.authUserId, // has the participant claimed an Auth account yet?
    }))
    .sort((a, b) => a.name.localeCompare(b.name));
}

// Distinct student Auth uids with proven org-scoped data, plus lightweight counts. Never fabricates identity.
export async function loadPreviewStudentCandidates(db, organizationId = '') {
  if (!organizationId) return []; // fail closed — never assume an org (no cross-org candidates)
  const byUid = new Map();
  const bump = (uid, key) => {
    if (!uid) return;
    const rec = byUid.get(uid) || { studentAuthUid: uid, videoCount: 0, lessonCount: 0, name: '' };
    rec[key] += 1;
    byUid.set(uid, rec);
  };
  try {
    const vids = await getDocs(query(collection(db, 'videos'), where('organizationId', '==', organizationId)));
    vids.forEach((d) => { const v = d.data(); bump(v.userId || v.studentId, 'videoCount'); });
  } catch (e) { /* read failure is not "no students" — caller shows an honest error, never a fake empty */ }
  try {
    const lessons = await getDocs(query(collection(db, 'lessonInstances'), where('organizationId', '==', organizationId)));
    lessons.forEach((d) => { const l = d.data(); if (l.studentId) bump(l.studentId, 'lessonCount'); });
  } catch (e) { /* same */ }
  return [...byUid.values()].sort((a, b) => (b.videoCount + b.lessonCount) - (a.videoCount + a.lessonCount));
}

// Resolve display names for candidate uids from the users collection (read-only, best-effort). Never blocks the list.
export async function resolveCandidateNames(db, candidates = []) {
  const out = [];
  for (const c of candidates) {
    let name = '';
    try {
      const snap = await getDocs(query(collection(db, 'users'), where('uid', '==', c.studentAuthUid)));
      name = snap.docs[0]?.data()?.name || snap.docs[0]?.data()?.displayName || '';
    } catch (e) { /* ignore */ }
    out.push({ ...c, name: name || `Student ${String(c.studentAuthUid).slice(0, 6)}` });
  }
  return out;
}
